import { DeleteAgentDialog } from "@app/components/assistant/DeleteAgentDialog";
import { useSendNotification } from "@app/hooks/useNotification";
import { useAuth } from "@app/lib/auth/AuthContext";
import { clientFetch } from "@app/lib/egress/client";
import { useAppRouter } from "@app/lib/platform";
import { useUpdateUserFavorite } from "@app/lib/swr/assistants";
import { hasHealthyProviders } from "@app/lib/utils/providersHealth";
import {
  getAgentBuilderRoute,
  getConversationRoute,
} from "@app/lib/utils/router";
import logger from "@app/logger/logger";
import type { LightAgentConfigurationType } from "@app/types/assistant/agent";
import { canShowAgentConversationActions } from "@app/types/assistant/assistant";
import { normalizeError } from "@app/types/shared/utils/error_utils";
import type { WorkspaceType } from "@app/types/user";
import { isAdmin } from "@app/types/user";
import {
  Brackets,
  Button,
  Clipboard,
  DotsHorizontal,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Edit04,
  File02,
  MessagePlusCircle,
  Spinner,
  Star01,
  StarFilled,
  Trash01,
} from "@dust-tt/sparkle";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface AgentDetailsButtonBarProps {
  agentConfiguration: LightAgentConfigurationType;
  owner: WorkspaceType;
  isAgentConfigurationValidating: boolean;
  onClose: () => void;
}

export function AgentDetailsButtonBar({
  agentConfiguration,
  isAgentConfigurationValidating,
  owner,
  onClose,
}: AgentDetailsButtonBarProps) {
  const { t } = useTranslation();
  const { user, providersHealth } = useAuth();
  const router = useAppRouter();

  const { updateUserFavorite, isUpdatingFavorite } = useUpdateUserFavorite({
    owner,
    agentConfigurationId: agentConfiguration.sId,
  });

  if (
    !agentConfiguration ||
    agentConfiguration.status === "archived" ||
    !user
  ) {
    return null;
  }

  const canEditAgent = agentConfiguration.canEdit || isAdmin(owner);

  const isFavoriteDisabled =
    isAgentConfigurationValidating || isUpdatingFavorite;

  const agentIsFavorite = agentConfiguration.userFavorite || isFavoriteDisabled;

  const handleNewConversation = async () => {
    onClose();
    await router.push(
      getConversationRoute(owner.sId, "new", `agent=${agentConfiguration.sId}`)
    );
  };

  return (
    <div className="flex flex-row items-center gap-2 px-1.5">
      <Button
        icon={agentIsFavorite ? StarFilled : Star01}
        tooltip={
          agentIsFavorite
            ? t("assistant.removeFromFavorites", "Remove from favorites")
            : t("assistant.addToFavorites", "Add to favorites")
        }
        size="sm"
        variant="outline"
        disabled={isFavoriteDisabled}
        onClick={() => updateUserFavorite(!agentConfiguration.userFavorite)}
      />

      {canShowAgentConversationActions(agentConfiguration.sId) && (
        <Button
          icon={MessagePlusCircle}
          size="sm"
          variant="outline"
          tooltip={t("assistant.newConversation", "New conversation")}
          onClick={handleNewConversation}
        />
      )}

      {agentConfiguration.scope !== "global" && (
        <Button
          size="sm"
          tooltip={t("assistant.editAgent", "Edit agent")}
          href={
            canEditAgent
              ? getAgentBuilderRoute(owner.sId, agentConfiguration.sId)
              : undefined
          }
          disabled={!canEditAgent || !hasHealthyProviders(providersHealth)}
          variant="outline"
          icon={Edit04}
        />
      )}

      {agentConfiguration.scope !== "global" && (
        <AgentDetailsDropdownMenu
          showTrigger
          agentConfiguration={agentConfiguration}
          owner={owner}
        />
      )}
    </div>
  );
}

interface AgentDetailsDropdownMenuProps {
  agentConfiguration?: LightAgentConfigurationType;
  owner: WorkspaceType;
  showTrigger?: boolean;
  onClose?: () => void;
  showEditOption?: boolean;
  contextMenuPosition?: { x: number; y: number };
}

export function AgentDetailsDropdownMenu({
  agentConfiguration,
  owner,
  showTrigger = false,
  onClose,
  showEditOption = false,
  contextMenuPosition,
}: AgentDetailsDropdownMenuProps) {
  const { t } = useTranslation();
  const sendNotification = useSendNotification();
  const router = useAppRouter();

  const { providersHealth } = useAuth();
  const noHealthyProviders = !hasHealthyProviders(providersHealth);

  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  if (!agentConfiguration || agentConfiguration.status === "archived") {
    return false;
  }

  const allowDeletion = agentConfiguration?.canEdit || isAdmin(owner);
  const canEditAgent = agentConfiguration?.canEdit || isAdmin(owner);

  const handleExportToYAML = async () => {
    setIsExporting(true);
    const response = await clientFetch(
      `/api/w/${owner.sId}/assistant/agent_configurations/${agentConfiguration?.sId}/export/yaml`
    );

    if (!response.ok) {
      const errorData = await response.json();
      sendNotification({
        title: t("assistant.exportFailed", "Export failed"),
        description:
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          errorData.error?.message ||
          t(
            "assistant.exportErrorOccurred",
            "An error occurred while exporting"
          ),
        type: "error",
      });
      setIsExporting(false);
      return;
    }

    const { yamlContent, filename } = await response.json();
    try {
      const blob = new Blob([yamlContent], { type: "application/yaml" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      sendNotification({
        title: t("assistant.exportSuccessful", "Export successful"),
        description: t("assistant.agentExportedToYaml", {
          defaultValue: 'Agent "{{agentName}}" exported to YAML',
          agentName: agentConfiguration.name,
        }),
        type: "success",
      });
    } catch (error) {
      sendNotification({
        title: t("assistant.exportFailed", "Export failed"),
        description:
          normalizeError(error).message ||
          t(
            "assistant.exportErrorOccurred",
            "An error occurred while exporting"
          ),
        type: "error",
      });

      logger.error(
        { workspaceId: owner.sId, agentId: agentConfiguration.sId },
        "Failed to export agent configuration to YAML"
      );
    } finally {
      setIsExporting(false);
    }
  };

  const menuItems = agentConfiguration && (
    <>
      {showEditOption &&
        agentConfiguration.scope !== "global" &&
        canEditAgent && (
          <DropdownMenuItem
            label={t("assistant.editAgent", "Edit agent")}
            disabled={noHealthyProviders}
            onClick={(e) => {
              e.stopPropagation();
              void router.push(
                getAgentBuilderRoute(owner.sId, agentConfiguration.sId)
              );
              onClose?.();
            }}
            icon={Edit04}
          />
        )}
      <DropdownMenuItem
        label={t("assistant.copyAgentId", "Copy agent ID")}
        onClick={async (e) => {
          e.stopPropagation();
          await navigator.clipboard.writeText(agentConfiguration.sId);
          onClose?.();
        }}
        icon={Brackets}
      />
      <DropdownMenuItem
        label={
          isExporting
            ? t("assistant.exporting", "Exporting...")
            : t("assistant.exportToYaml", "Export to YAML")
        }
        onClick={(e) => {
          e.stopPropagation();
          void handleExportToYAML();
          onClose?.();
        }}
        icon={isExporting ? <Spinner size="xs" /> : File02}
        disabled={isExporting}
      />
      {agentConfiguration.scope !== "global" && (
        <>
          <DropdownMenuItem
            label={t("assistant.duplicateNew", "Duplicate (New)")}
            disabled={noHealthyProviders}
            data-gtm-label="agentDuplicationButton"
            data-gtm-location="agentDetails"
            icon={Clipboard}
            onClick={async (e) => {
              e.stopPropagation();
              onClose?.();
              await router.push(
                getAgentBuilderRoute(
                  owner.sId,
                  "new",
                  `duplicate=${agentConfiguration.sId}`
                )
              );
            }}
          />
          {allowDeletion && (
            <DropdownMenuItem
              label={t("assistant.archive", "Archive")}
              icon={Trash01}
              onClick={(e) => {
                e.stopPropagation();
                setShowDeletionModal(true);
              }}
              variant="warning"
            />
          )}
        </>
      )}
    </>
  );

  // Context menu version
  return (
    <>
      <DeleteAgentDialog
        owner={owner}
        isOpen={showDeletionModal}
        agentConfiguration={agentConfiguration}
        onClose={() => {
          setShowDeletionModal(false);
          onClose?.();
        }}
      />
      {contextMenuPosition && agentConfiguration ? (
        <DropdownMenu
          open={true}
          onOpenChange={(open) => !open && onClose?.()}
          modal={false}
        >
          <DropdownMenuTrigger asChild>
            <div
              style={{
                position: "fixed",
                left: contextMenuPosition.x,
                top: contextMenuPosition.y,
                width: 0,
                height: 0,
                pointerEvents: "none",
              }}
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent>{menuItems}</DropdownMenuContent>
        </DropdownMenu>
      ) : showTrigger ? (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button icon={DotsHorizontal} size="sm" variant="outline" />
          </DropdownMenuTrigger>
          <DropdownMenuContent>{menuItems}</DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </>
  );
}
